import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  TransitLayer,
  OverlayViewF,
} from "@react-google-maps/api";
import { useLocation } from "react-router-dom";
import {
  accknowladgeAccidentId,
} from "../../../Services/EndPoints";
import { putData } from "../../../Services/Services";

// ─── Shared Google Maps loader (fixes "Loader must not be called again" / white screen) ──
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Constants ────────────────────────────────────────────────────────────────

const MAP_CONTAINER_STYLE = { height: "711px", width: "100%" };
const DEFAULT_CENTER      = { lat: 17.385044, lng: 78.486671 };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getEventTypeConfig = (eventType) => {
  const configs = {
    Impact: {
      label: "Impact", color: "#dc3545",
      icon: "fas fa-exclamation-triangle",
      bgGradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
    },
    SOS: {
      label: "SOS", color: "#ff0000",
      icon: "fas fa-phone-volume",
      bgGradient: "linear-gradient(135deg, #ff0000 0%, #cc0000 100%)",
    },
    rollover: {
      label: "Roll Over", color: "#dc3545",
      icon: "fas fa-car-crash",
      bgGradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
    },
    collision: {
      label: "Collision", color: "#dc3545",
      icon: "fas fa-car-burst",
      bgGradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
    },
  };
  return configs[eventType] || {
    label: "Accident", color: "#dc3545",
    icon: "fas fa-exclamation-triangle",
    bgGradient: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
  };
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return "N/A";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Invalid Date";
    const day    = String(date.getDate()).padStart(2, "0");
    const month  = String(date.getMonth() + 1).padStart(2, "0");
    const year   = String(date.getFullYear()).slice(-2);
    let hours    = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm   = hours >= 12 ? "PM" : "AM";
    hours = String(hours % 12 || 12).padStart(2, "0");
    return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
  } catch { return "N/A"; }
};

const getEventTypeDisplay = (eventType) => {
  const eventMap = {
    rollover: "Roll Over", role_over: "Roll Over",
    collision: "Collision", hard_braking: "Hard Braking",
    sudden_acceleration: "Sudden Acceleration", impact: "Impact Detected",
    overspeed: "Over Speeding", sharp_turn: "Sharp Turn",
  };
  return eventMap[eventType] || eventType?.replace(/_/g, " ") || "Unknown Event";
};

const getSeverityColor       = (speed) => parseFloat(speed) > 50 ? "#dc3545" : "#fd7e14";
const getSeverityLevel       = (speed) => parseFloat(speed) > 50 ? "CRITICAL" : "HIGH";
const getSeverityDescription = (speed) =>
  parseFloat(speed) > 50
    ? "Critical speed incident - Immediate emergency response required"
    : "High speed incident - Emergency services notified";

const getStatusDisplay = (status) =>
  ({ RESOLVED: "Resolved", ACCEPT: "Accepted", PENDING: "Pending", "": "Pending", null: "Pending", undefined: "Pending" })[status] ?? "Pending";

const getStatusColor = (status) =>
  ({ RESOLVED: "#28a745", ACCEPT: "#17a2b8", PENDING: "#ffc107", "": "#ffc107", null: "#ffc107", undefined: "#ffc107" })[status] ?? "#ffc107";

// ─── Loading Overlay ──────────────────────────────────────────────────────────

const LoadingOverlay = ({ message = "Loading..." }) => (
  <div style={{
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(255,255,255,0.9)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, borderRadius: "8px",
  }}>
    <div className="text-center">
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="text-muted mb-0">{message}</p>
    </div>
  </div>
);

// ─── Map Type Selector ────────────────────────────────────────────────────────

const MAP_TYPE_OPTIONS = [
  {
    id: "roadmap", label: "Standard",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

const CustomLayerControl = ({ mapType, setMapType }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "absolute", top: 60, right: 10, zIndex: 1000 }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: 40, height: 40, border: "none", background: "white",
          borderRadius: 8, cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: 45, right: 0,
          background: "white", borderRadius: 10, padding: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 160,
          border: "1px solid #e2e8f0",
        }}>
          {MAP_TYPE_OPTIONS.map((opt) => (
            <div
              key={opt.id}
              onClick={() => { setMapType(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", cursor: "pointer", borderRadius: 7,
                background: mapType === opt.id ? "#f0fdfb" : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ color: mapType === opt.id ? "#1a7a73" : "#94a3b8" }}>{opt.icon}</span>
              <span style={{
                fontSize: 13,
                fontWeight: mapType === opt.id ? 600 : 400,
                color: mapType === opt.id ? "#1a7a73" : "#334155",
              }}>{opt.label}</span>
              {mapType === opt.id && (
                <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AccidentViewer = () => {
  const location = useLocation();

  const [mapCenter,        setMapCenter]        = useState(DEFAULT_CENTER);
  const [mapZoom,          setMapZoom]          = useState(15);
  const [mapType,          setMapType]          = useState("roadmap");
  const [address,          setAddress]          = useState("");
  const [nearestLandmark,  setNearestLandmark]  = useState("");
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [isLoadingLandmark,setIsLoadingLandmark]= useState(false);
  const [accidentData,     setAccidentData]     = useState(null);
  const [isAcknowledging,  setIsAcknowledging]  = useState(false);
  const [isAcknowledged,   setIsAcknowledged]   = useState(false);
  const [currentStatus,    setCurrentStatus]    = useState("");
  const [showInfoWindow,   setShowInfoWindow]   = useState(false);
  const [showAckModal,     setShowAckModal]     = useState(false);
  const [ackComment,       setAckComment]       = useState("");

  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching,   setIsSearching]   = useState(false);
  const [showTransit,   setShowTransit]   = useState(false);
  const [isFullscreen,  setIsFullscreen]  = useState(false);

  const mapRef           = useRef(null);
  const geocoderRef      = useRef(null);
  const autocompleteRef  = useRef(null);
  const searchTimerRef   = useRef(null);
  const mapContainerRef  = useRef(null);

  // ── Shared loader — MUST include id + libraries to avoid white screen ─────
  const { isLoaded, loadError } = useJsApiLoader({
    id:               GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries:        GOOGLE_MAPS_LIBRARIES,
  });

  // ── Initialise Geocoder as soon as Maps JS is ready ──────────────────────
  // Must be in a useEffect — NOT in onMapLoad — so it is available before
  // the address-fetch effect fires (both depend on isLoaded becoming true,
  // but the map render/onMapLoad callback comes slightly later).
  useEffect(() => {
    if (isLoaded && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      if (!autocompleteRef.current) {
        autocompleteRef.current = new window.google.maps.places.AutocompleteService();
      }
    }
  }, [isLoaded]);

  // ── Location search ───────────────────────────────────────────────────────

  const searchLocation = useCallback((query) => {
    if (!query.trim() || !autocompleteRef.current) { setSearchResults([]); return; }
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" } },
      (predictions, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  }, []);

  const selectSearchResult = useCallback((place) => {
    setSearchQuery(place.description);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const viewport = results[0].geometry.viewport;
        if (mapRef.current) {
          if (viewport) {
            mapRef.current.fitBounds(viewport);
          } else {
            mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() });
            mapRef.current.setZoom(15);
          }
        }
      }
    });
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(val), 500);
  }, [searchLocation]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // PlacesService requires the map instance — used by getNearestLandmark.
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // ── Google Maps Geocoder helper ───────────────────────────────────────────
  const getAddressFromLatLng = useCallback((lat, lng) => {
    return new Promise((resolve) => {
      const attempt = (retries) => {
        if (!geocoderRef.current) {
          // Geocoder not ready yet — retry up to 10 × 200 ms
          if (retries > 0) {
            setTimeout(() => attempt(retries - 1), 200);
          } else {
            resolve(`${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`);
          }
          return;
        }
        geocoderRef.current.geocode(
          { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
          (results, status) => {
            if (status === "OK" && results && results[0]) {
              const components = results[0].address_components;
              const get = (...types) => {
                for (const type of types) {
                  const c = components.find((ac) => ac.types.includes(type));
                  if (c) return c.long_name;
                }
                return null;
              };
              const road    = get("route", "premise", "establishment");
              const area    = get("sublocality_level_1", "sublocality", "neighborhood");
              const city    = get("locality", "administrative_area_level_3");
              const state   = get("administrative_area_level_1");
              const pincode = get("postal_code");
              const parts   = [road, area, city, state, pincode].filter(Boolean);
              resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
            } else {
              resolve(`${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`);
            }
          }
        );
      };
      attempt(10);
    });
  }, []);

  // ── Nearest landmark via Google Places nearbySearch ───────────────────────
  const getNearestLandmark = useCallback((lat, lng) => {
    return new Promise((resolve) => {
      const attempt = (retries) => {
        if (!window.google?.maps?.places || !mapRef.current) {
          // Map not mounted yet — retry up to 10 × 300 ms
          if (retries > 0) {
            setTimeout(() => attempt(retries - 1), 300);
          } else {
            resolve("Landmark information not available");
          }
          return;
        }
        const service = new window.google.maps.places.PlacesService(mapRef.current);
        service.nearbySearch(
          { location: { lat: parseFloat(lat), lng: parseFloat(lng) }, radius: 500, type: ["point_of_interest"] },
          (results, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              results && results.length > 0
            ) {
              resolve(results[0].name);
            } else {
              resolve("Landmark information not available");
            }
          }
        );
      };
      attempt(10);
    });
  }, []);

  // ── Load accident data ────────────────────────────────────────────────────
  useEffect(() => {
    if (location.state?.accidentData) {
      const data = location.state.accidentData;
      setAccidentData(data);
      const acknowledgedStatus = data.acknowledged;
      setCurrentStatus(acknowledgedStatus || "");
      setIsAcknowledged(acknowledgedStatus === "RESOLVED");
    }
  }, [location.state]);

  // ── Update map center + fetch address/landmark when data arrives ──────────
  useEffect(() => {
    if (!accidentData || !isLoaded) return;
    const lat = accidentData.vehicleData?.latitude  || accidentData.latitude;
    const lng = accidentData.vehicleData?.longitude || accidentData.longitude;
    if (!lat || !lng) return;

    const latF = parseFloat(lat);
    const lngF = parseFloat(lng);
    setMapCenter({ lat: latF, lng: lngF });
    setMapZoom(15);

    // Address
    setIsLoadingAddress(true);
    getAddressFromLatLng(latF, lngF)
      .then(setAddress)
      .catch(() => setAddress(`${latF.toFixed(6)}, ${lngF.toFixed(6)}`))
      .finally(() => setIsLoadingAddress(false));

    // Landmark — only once Places service is ready (map must be loaded)
    setIsLoadingLandmark(true);
    getNearestLandmark(latF, lngF)
      .then(setNearestLandmark)
      .catch(() => setNearestLandmark("Landmark information not available"))
      .finally(() => setIsLoadingLandmark(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accidentData, isLoaded]);

  // ── Derived helpers ───────────────────────────────────────────────────────
  const getAccidentField = (field) => {
    if (!accidentData) return null;
    return accidentData.vehicleData?.[field] !== undefined
      ? accidentData.vehicleData[field]
      : accidentData[field];
  };

  // ── Acknowledge ───────────────────────────────────────────────────────────
  const acknowledgeAccident = async (actionTaken) => {
    if (isAcknowledged) return;
    const accidentId = accidentData?.accidentId;
    try {
      setIsAcknowledging(true);
      const response = await putData(
        accknowladgeAccidentId.replace("ID", accidentId),
        { actionTaken }
      );
      if (response) {
        setIsAcknowledged(true);
        setCurrentStatus("ACCEPT");
        setShowAckModal(false);
      }
    } catch (error) {
      console.error("Error acknowledging accident:", error);
    } finally {
      setIsAcknowledging(false);
    }
  };

  // ── No data guard ─────────────────────────────────────────────────────────
  if (!accidentData) {
    return (
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <i className="fas fa-exclamation-triangle fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No Accident Data Available</h5>
              <p className="text-muted">Please check the accident notification or try again.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const eventType      = getAccidentField("eventType");
  const speed          = getAccidentField("speed");
  const vehicleNumber  = getAccidentField("vehicleNumber");
  const trackingTime   = getAccidentField("vehicleTrackingTime");
  const latitude       = getAccidentField("latitude");
  const longitude      = getAccidentField("longitude");
  const accidentId     = accidentData.accidentId;

  const severityColor       = getSeverityColor(speed);
  const severityLevel       = getSeverityLevel(speed);
  const severityDescription = getSeverityDescription(speed);
  const eventConfig         = getEventTypeConfig(eventType);
  const isAcknowledgeDisabled = isAcknowledged || isAcknowledging;

  const markerPosition =
    latitude && longitude
      ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
      : null;

  // Custom SVG marker icon
  const accidentMarkerIcon = isLoaded
    ? {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15" fill="${eventConfig.color}" stroke="white" stroke-width="3"/>
            <text x="18" y="23" text-anchor="middle" font-size="14" fill="white" font-family="Arial">!</text>
          </svg>
        `)}`,
        scaledSize: new window.google.maps.Size(36, 36),
        anchor:     new window.google.maps.Point(18, 18),
      }
    : null;

  // ── Shared styles ─────────────────────────────────────────────────────────
  const sectionHeaderStyle = {
    display: 'flex', alignItems: 'center', gap: '7px',
    fontWeight: '700', fontSize: '12px', color: '#2d3748',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    borderBottom: '1px solid #e8ecf0', paddingBottom: '8px',
    marginBottom: '10px',
  };

  const infoLabelStyle = {
    fontSize: '11px', fontWeight: '600', color: '#718096',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    marginBottom: '4px',
  };

  const infoValueStyle = {
    fontSize: '13px', color: '#2d3748', fontWeight: '500',
  };

  const infoBoxStyle = {
    flex: 1,
  };

  const badgeStyle = {
    display: 'inline-block', borderRadius: '20px',
    padding: '4px 14px', fontSize: '12px', fontWeight: '700',
    letterSpacing: '0.3px',
  };

  const addressBoxStyle = {
    borderRadius: '8px', padding: '8px 10px',
    fontSize: '12px', color: '#444', lineHeight: '1.5',
    minHeight: '54px',
  };

  const sectionCardHeader = (accentColor) => ({
    display: 'flex', alignItems: 'center', gap: '7px',
    fontWeight: '700', fontSize: '11px', color: accentColor,
    textTransform: 'uppercase', letterSpacing: '0.6px',
    paddingBottom: '9px', marginBottom: '10px',
    borderBottom: `2px solid ${accentColor}22`,
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '12px 16px 4px' }}>
      <div className="row g-3">

        {/* RIGHT — MAP CARD */}
        <div className="col-md-7 order-2">
          <div style={{
            border: 'none', borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
          }}>
            {/* Map card header */}
            <div style={{
              background: eventConfig.bgGradient,
              color: 'white',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', fontSize: '14px', flex: 1 }}>
                <i className={eventConfig.icon}></i>
                {eventType === "rollover" ? "Accident Location" : `${eventConfig.label} Location`}
              </div>
              <span style={{
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.30)',
                borderRadius: '20px',
                padding: '2px 9px',
                fontSize: '10px',
                fontWeight: '600',
                flexShrink: 0,
              }}>
                {(MAP_TYPE_OPTIONS.find(o => o.id === mapType)?.label ?? mapType)} layer
              </span>
              <span style={{
                background: 'rgba(255,255,255,0.20)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '20px',
                padding: '3px 12px',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '0.3px',
                flexShrink: 0,
              }}>
                ID: {accidentId || "N/A"}
              </span>
            </div>

            {/* Search bar */}
            <div style={{ padding: "8px 12px", background: "#fff", borderBottom: "1px solid #e2e8f0", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "6px 10px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search city, area, landmark or coordinates…"
                  style={{ flex: 1, border: "none", outline: "none", fontSize: 13, color: "#0f172a", background: "transparent" }}
                />
                {isSearching && (
                  <div style={{ width: 14, height: 14, border: "2px solid #e2e8f0", borderTop: "2px solid #0f4a45", borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0 }} />
                )}
                {searchQuery && !isSearching && (
                  <button onClick={clearSearch} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", display: "flex", alignItems: "center" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
              {searchResults.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 12, right: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "0 0 10px 10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 1050, maxHeight: 260, overflowY: "auto" }}>
                  {searchResults.map((result) => (
                    <div
                      key={result.place_id}
                      onClick={() => selectSearchResult(result)}
                      style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444" stroke="none" style={{ flexShrink: 0, marginTop: 2 }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{result.structured_formatting?.main_text || result.description}</div>
                        {result.structured_formatting?.secondary_text && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{result.structured_formatting.secondary_text}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div ref={mapContainerRef} style={{ position: 'relative' }}>
              {!isLoaded && <LoadingOverlay message="Loading Google Maps..." />}
              {loadError && (
                <div className="alert alert-danger m-3">
                  Failed to load Google Maps. Please check your API key.
                </div>
              )}

              {isLoaded && (
                <div style={{ position: "relative" }}>
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={mapCenter}
                    zoom={mapZoom}
                    mapTypeId={mapType}
                    onLoad={onMapLoad}
                    onClick={() => setShowInfoWindow(false)}
                    options={{
                      mapTypeControl:    false,
                      streetViewControl: true,
                      fullscreenControl: false,
                      zoomControl:       true,
                      clickableIcons:    false,
                      disableDefaultUI:  false,
                      gestureHandling:   "greedy",
                    }}
                  >
                    {markerPosition && (
                      <>
                        <Marker
                          position={markerPosition}
                          icon={accidentMarkerIcon}
                          onClick={(e) => { e.stop?.(); setShowInfoWindow((p) => !p); }}
                          animation={window.google.maps.Animation.BOUNCE}
                        />
                        <Circle
                          center={markerPosition}
                          radius={200}
                          options={{
                            strokeColor:   severityColor,
                            strokeOpacity: 0.6,
                            strokeWeight:  2,
                            fillColor:     severityColor,
                            fillOpacity:   0.08,
                          }}
                        />

                        {/* Info Panel - anchored above the marker, moves with the map */}
                        {showInfoWindow && (
                          <OverlayViewF
                            position={markerPosition}
                            mapPaneName="floatPane"
                            getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h + 34) })}
                          >
                    <div
                      ref={(el) => el && window.google.maps.OverlayView.preventMapHitsAndGesturesFrom(el)}
                      style={{ position: "relative", width: "280px", cursor: "default" }}
                    >
                    <div style={{
                      width: "280px",
                      background: "#fff", borderRadius: "12px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.22)",
                      overflow: "hidden", fontFamily: "sans-serif",
                      fontSize: "13px", animation: "fadeInPanel 0.18s ease",
                    }}>
                      <div style={{
                        background: eventConfig.bgGradient, color: "white",
                        padding: "10px 14px",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}>
                        <strong style={{ fontSize: "13px", letterSpacing: "0.3px" }}>
                          <i className={`${eventConfig.icon} me-2`}></i>
                          Accident Details
                        </strong>
                        <button
                          onClick={() => setShowInfoWindow(false)}
                          style={{
                            background: "rgba(255,255,255,0.25)", border: "none",
                            color: "white", borderRadius: "50%",
                            width: "22px", height: "22px", cursor: "pointer",
                            fontSize: "13px", padding: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >✕</button>
                      </div>

                      <div style={{ padding: "12px 14px" }}>
                        {[
                          ["Vehicle", vehicleNumber || "N/A"],
                          ["Type",    getEventTypeDisplay(eventType)],
                          ["Speed",   `${speed || "0"} km/h`],
                          ["Time",    formatDateTime(trackingTime || accidentData.accidentTime)],
                        ].map(([label, value]) => (
                          <div key={label} style={{
                            display: "flex", justifyContent: "space-between",
                            padding: "4px 0", borderBottom: "1px solid #f0f0f0",
                          }}>
                            <span style={{ color: "#888", fontWeight: 600 }}>{label}</span>
                            <span style={{ color: "#222", textAlign: "right", maxWidth: "160px" }}>{value}</span>
                          </div>
                        ))}

                        <div style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "6px 0", borderBottom: "1px solid #f0f0f0",
                        }}>
                          <span style={{ color: "#888", fontWeight: 600 }}>Status</span>
                          <span style={{
                            background: getStatusColor(currentStatus),
                            color: "white", padding: "2px 10px",
                            borderRadius: "20px", fontSize: "11px", fontWeight: 700,
                          }}>
                            {getStatusDisplay(currentStatus)}
                          </span>
                        </div>

                        {address && (
                          <div style={{
                            marginTop: "8px", padding: "7px 10px",
                            background: "#f8f9fa", borderRadius: "8px",
                            fontSize: "12px", color: "#444", lineHeight: "1.5",
                          }}>
                            <span style={{ fontWeight: 700 }}>📍 Address</span>
                            <div style={{ marginTop: "2px" }}>
                              {isLoadingAddress ? "Loading..." : address}
                            </div>
                          </div>
                        )}

                        {nearestLandmark && nearestLandmark !== "Landmark information not available" && (
                          <div style={{
                            marginTop: "6px", padding: "7px 10px",
                            background: "#fff8e1", borderRadius: "8px",
                            fontSize: "12px", color: "#555",
                            border: "1px solid #ffe082", lineHeight: "1.5",
                          }}>
                            <span style={{ fontWeight: 700 }}>🏛 Landmark</span>
                            <div style={{ marginTop: "2px" }}>
                              {isLoadingLandmark ? "Finding landmark..." : nearestLandmark}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Pointer arrow towards the marker */}
                    <div style={{
                      position: "absolute", left: "50%", bottom: "-8px",
                      transform: "translateX(-50%) rotate(45deg)",
                      width: "16px", height: "16px", background: "#fff",
                      boxShadow: "3px 3px 6px rgba(0,0,0,0.12)",
                    }} />
                    </div>
                          </OverlayViewF>
                        )}
                      </>
                    )}
                    {showTransit && <TransitLayer />}
                  </GoogleMap>

                  <CustomLayerControl mapType={mapType} setMapType={setMapType} />
                  <button
                    onClick={toggleFullscreen}
                    title={isFullscreen ? "Exit fullscreen" : "Full screen"}
                    style={{
                      position: "absolute", top: 10, right: 10, zIndex: 1000,
                      width: 40, height: 40, borderRadius: 8, border: "none", cursor: "pointer",
                      background: isFullscreen ? "#1a7a73" : "white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    {isFullscreen ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                        <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
                        <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => setShowTransit(p => !p)}
                    title={showTransit ? "Hide transit" : "Show transit"}
                    style={{
                      position: "absolute", top: 110, right: 10, zIndex: 1000,
                      width: 40, height: 40, borderRadius: 8, border: "none", cursor: "pointer",
                      background: showTransit ? "#1a7a73" : "white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                      stroke={showTransit ? "#fff" : "#555"} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="2" width="16" height="16" rx="2"/>
                      <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                      <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* LEFT — ACCIDENT DETAILS */}
        <div className="col-md-5 order-1">
          <div style={{
            border: 'none', borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
            background: '#ffffff',
          }}>
            {/* Panel header */}
            <div style={{
              background: eventConfig.bgGradient,
              color: 'white',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', fontSize: '14px' }}>
                <i className={eventConfig.icon}></i>
                {eventType === "rollover" ? "Accident Details" : `${eventConfig.label} Details`}
              </div>
              <span style={{
                background: 'rgba(255,255,255,0.20)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '20px',
                padding: '3px 12px',
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}>
                {getStatusDisplay(currentStatus)}
              </span>
            </div>

            <div style={{ padding: '10px', background: '#f4f7fb' }}>

              {/* SEVERITY BANNER */}
              <div style={{
                background: `linear-gradient(135deg, ${severityColor}18 0%, ${severityColor}08 100%)`,
                border: `1px solid ${severityColor}35`,
                borderLeft: `5px solid ${severityColor}`,
                borderRadius: '10px',
                padding: '11px 14px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%',
                  background: `${severityColor}20`,
                  border: `2px solid ${severityColor}40`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: severityColor, fontSize: '15px', paddingLeft:'0' }}></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span style={{ fontWeight: '800', color: severityColor, fontSize: '13px' }}>{severityLevel} Severity</span>
                    <span style={{
                      background: severityColor, color: 'white',
                      borderRadius: '20px', padding: '2px 10px',
                      fontSize: '10px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase',
                    }}>{severityLevel}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>{severityDescription}</div>
                </div>
              </div>

              {/* EMERGENCY ACTIONS CARD */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '11px 13px', marginBottom: '8px', border: '1px solid #e8ecf0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={sectionCardHeader('#dc3545')}>
                  <i className="fas fa-ambulance"></i>
                  Emergency Actions
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {/* ACKNOWLEDGE */}
                  <button
                    className={!isAcknowledgeDisabled ? "blink-alert" : ""}
                    style={{
                      background: isAcknowledgeDisabled
                        ? 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)'
                        : 'linear-gradient(135deg, #d9534f 0%, #c0392b 100%)',
                      color: 'white', border: 'none', borderRadius: '8px',
                      padding: '9px 14px', fontSize: '13px', fontWeight: '600',
                      flex: 1, cursor: isAcknowledgeDisabled ? 'not-allowed' : 'pointer',
                      opacity: isAcknowledgeDisabled ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      boxShadow: isAcknowledgeDisabled ? 'none' : '0 3px 8px rgba(217,83,79,0.32)',
                    }}
                    onClick={() => { if (!isAcknowledgeDisabled) { setAckComment(""); setShowAckModal(true); } }}
                    disabled={isAcknowledgeDisabled}
                  >
                    {isAcknowledging ? (
                      <><div className="spinner-border spinner-border-sm" role="status"></div> Processing...</>
                    ) : isAcknowledged ? (
                      <><i className="fas fa-check-double" style={{ paddingLeft: 0 }}></i> Acknowledged</>
                    ) : (
                      <><i className="fas fa-check-circle" style={{ paddingLeft: 0 }}></i> Acknowledge</>
                    )}
                  </button>

                  {/* CALL DRIVER */}
                  <div style={{
                    flex: 1, background: '#FFFBF0',
                    border: '1px solid #FFECB3', borderRadius: '8px',
                    padding: '8px 11px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px',
                  }}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#b7791f', marginBottom: '2px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <i className="fas fa-user me-1" style={{ paddingLeft: 0 }}></i> Driver
                      </div>
                      <div style={{ fontSize: '12px', color: '#333', fontWeight: '600', fontFamily: 'monospace' }}>
                        {accidentData.driverMobile || "9898989898"}
                      </div>
                    </div>
                    <button
                      className="btn btn-warning btn-sm fw-bold"
                      style={{ padding: '5px 10px', fontSize: '12px', borderRadius: '7px', whiteSpace: 'nowrap' }}
                    >
                      <i className="fas fa-phone me-1" style={{ paddingLeft: 0 }}></i>Call
                    </button>
                  </div>
                </div>
              </div>

              {/* VEHICLE INFO CARD */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '11px 13px', marginBottom: '8px', border: '1px solid #e8ecf0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={sectionCardHeader('#0f4a45')}>
                  <i className="fas fa-car"></i>
                  Vehicle Information
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {/* Vehicle Number tile */}
                  <div style={{
                    flex: 1, background: '#EBF4FF', borderRadius: '8px',
                    padding: '10px 12px', border: '1px solid #BEE3F8', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#2b6cb0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>
                      <i className="fas fa-id-card me-1" style={{ paddingLeft: 0 }}></i> Vehicle No.
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#1a365d', letterSpacing: '0.8px' }}>
                      {vehicleNumber || "N/A"}
                    </div>
                  </div>
                  {/* Event Type tile */}
                  <div style={{
                    flex: 1, background: `${severityColor}12`, borderRadius: '8px',
                    padding: '10px 12px', border: `1px solid ${severityColor}30`, textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: severityColor, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>
                      <i className="fas fa-bolt me-1" style={{ paddingLeft: 0 }}></i> Event Type
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '800', color: severityColor }}>
                      {getEventTypeDisplay(eventType)}
                    </div>
                  </div>
                </div>
              </div>

              {/* LOCATION & TIME CARD */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '11px 13px', border: '1px solid #e8ecf0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={sectionCardHeader('#28a745')}>
                  <i className="fas fa-map-marker-alt"></i>
                  Location &amp; Time
                </div>

                {/* Address */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#38a169', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}>
                    <i className="fas fa-map-pin me-1"></i> Address
                  </div>
                  <div style={{
                    background: '#f0fdf4', border: '1px solid #c6f6d5',
                    borderRadius: '8px', padding: '9px 12px',
                    fontSize: '12px', color: '#276749', lineHeight: '1.5',
                  }}>
                    {isLoadingAddress ? (
                      <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="spinner-border spinner-border-sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                        Loading address...
                      </span>
                    ) : (address || "Address not available")}
                  </div>
                </div>

                {/* Nearest Landmark */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#2b6cb0', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}>
                    <i className="fas fa-landmark me-1"></i> Nearest Landmark
                  </div>
                  <div style={{
                    background: '#e8f4fd', border: '1px solid #bee3f8',
                    borderRadius: '8px', padding: '9px 12px',
                    fontSize: '12px', color: '#2c5282', lineHeight: '1.5',
                  }}>
                    {isLoadingLandmark ? (
                      <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className="spinner-border spinner-border-sm text-info" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                        Finding nearest landmark...
                      </span>
                    ) : nearestLandmark}
                  </div>
                </div>

                {/* Coordinates + Date/Time grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                      <i className="fas fa-crosshairs me-1" style={{ paddingLeft: 0 }}></i> Coordinates
                    </div>
                    <div style={{ fontSize: '11px', color: '#2d3748', fontWeight: '600', fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: '1.4' }}>
                      {latitude && longitude
                        ? `${parseFloat(latitude).toFixed(5)}, ${parseFloat(longitude).toFixed(5)}`
                        : "N/A"}
                    </div>
                  </div>
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                      <i className="fas fa-clock me-1" style={{ paddingLeft: 0 }}></i> Date &amp; Time
                    </div>
                    <div style={{ fontSize: '12px', color: '#2d3748', fontWeight: '600', lineHeight: '1.4' }}>
                      {formatDateTime(trackingTime || accidentData.accidentTime)}
                    </div>
                  </div>
                </div>

                {/* Max Speed row */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: parseFloat(speed) > 50 ? '#fff5f5' : '#fff8f0',
                  border: `1px solid ${parseFloat(speed) > 50 ? '#fed7d7' : '#fbd38d'}`,
                  borderRadius: '8px', padding: '9px 12px',
                  marginBottom: accidentData?.actionTaken ? '8px' : 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fas fa-tachometer-alt" style={{ color: parseFloat(speed) > 50 ? '#dc3545' : '#fd7e14', fontSize: '16px' }}></i>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>Max Speed</span>
                  </div>
                  <span style={{
                    background: parseFloat(speed) > 50 ? '#dc3545' : '#fd7e14',
                    color: 'white', borderRadius: '20px',
                    padding: '4px 16px', fontSize: '14px', fontWeight: '800', letterSpacing: '0.3px',
                  }}>
                    {speed || "0"} <span style={{ fontSize: '11px', fontWeight: '600' }}>km/h</span>
                  </span>
                </div>

                {/* Action Taken */}
                {accidentData?.actionTaken && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '9px 12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                      <i className="fas fa-clipboard-check me-1"></i> Action Taken
                    </div>
                    <div style={{ fontSize: '12px', color: '#166534', lineHeight: '1.5' }}>{accidentData.actionTaken}</div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ACK MODAL */}
      {showAckModal && (
        <div style={{
          position: "fixed", inset: 0,
          backgroundColor: "rgba(10,20,40,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2000,
          animation: "fadeInPanel 0.15s ease",
        }}>
          <div style={{
            background: 'white',
            borderRadius: '14px',
            width: '480px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.30)',
          }}>

            {/* ── Header ── */}
            <div style={{
              background: eventConfig.bgGradient,
              color: 'white',
              padding: '16px 20px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontWeight: '800', fontSize: '16px', marginBottom: '4px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: 'rgba(255,255,255,0.20)',
                      border: '1.5px solid rgba(255,255,255,0.40)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <i className="fas fa-clipboard-check" style={{ fontSize: '14px' }}></i>
                    </div>
                    Acknowledge Incident
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.85, marginLeft: '41px' }}>
                    Confirm you have reviewed and responded to this incident
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !isAcknowledging && setShowAckModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.30)',
                    color: 'white', borderRadius: '50%',
                    width: '28px', height: '28px', flexShrink: 0,
                    cursor: isAcknowledging ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', marginLeft: '12px',
                  }}
                >✕</button>
              </div>
            </div>

            {/* ── Incident summary strip ── */}
            <div style={{
              background: '#f8fafc',
              borderBottom: '1px solid #e8ecf0',
              padding: '10px 20px',
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              <span style={{
                background: '#EBF4FF', color: '#1a365d',
                border: '1px solid #BEE3F8', borderRadius: '20px',
                padding: '3px 11px', fontSize: '12px', fontWeight: '700',
                display: 'inline-flex', alignItems: 'center', gap: '5px',
              }}>
                <i className="fas fa-car" style={{ fontSize: '10px' }}></i>
                {vehicleNumber || "N/A"}
              </span>
              <span style={{
                background: `${severityColor}15`, color: severityColor,
                border: `1px solid ${severityColor}35`, borderRadius: '20px',
                padding: '3px 11px', fontSize: '12px', fontWeight: '700',
                display: 'inline-flex', alignItems: 'center', gap: '5px',
              }}>
                <i className={`${eventConfig.icon}`} style={{ fontSize: '10px' }}></i>
                {getEventTypeDisplay(eventType)}
              </span>
              <span style={{
                background: severityColor, color: 'white',
                borderRadius: '20px',
                padding: '3px 11px', fontSize: '11px', fontWeight: '700',
                letterSpacing: '0.5px', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: '5px',
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '9px' }}></i>
                {severityLevel}
              </span>
              <span style={{
                background: '#fff8e1', color: '#b7791f',
                border: '1px solid #fce08a', borderRadius: '20px',
                padding: '3px 11px', fontSize: '12px', fontWeight: '700',
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                marginLeft: 'auto',
              }}>
                <i className="fas fa-tachometer-alt" style={{ fontSize: '10px' }}></i>
                {speed || "0"} km/h
              </span>
            </div>

            {/* ── Body ── */}
            <div style={{ padding: '18px 20px 14px' }}>
              <label style={{
                fontSize: '12px', fontWeight: '700', color: '#2d3748',
                textTransform: 'uppercase', letterSpacing: '0.5px',
                display: 'flex', alignItems: 'center', gap: '6px',
                marginBottom: '8px',
              }}>
                <i className="fas fa-pencil-alt" style={{ color: '#0f4a45', fontSize: '11px' }}></i>
                Action Taken
                <span style={{ color: '#dc3545', fontSize: '14px', lineHeight: 1 }}>*</span>
              </label>
              <textarea
                rows="4"
                value={ackComment}
                onChange={(e) => setAckComment(e.target.value)}
                placeholder="Describe the action taken in response to this incident..."
                disabled={isAcknowledging}
                maxLength={255}
                style={{
                  width: '100%', borderRadius: '9px', fontSize: '13px',
                  resize: 'none', padding: '10px 13px', lineHeight: '1.6',
                  border: `1.5px solid ${ackComment.trim() ? '#0f4a45' : '#dee2e6'}`,
                  outline: 'none', color: '#333',
                  background: isAcknowledging ? '#f9fafb' : 'white',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                  boxShadow: ackComment.trim() ? '0 0 0 3px rgba(26,122,115,0.10)' : 'none',
                }}
              />

              {/* Character progress bar */}
              <div style={{ marginTop: '6px' }}>
                <div style={{
                  height: '3px', borderRadius: '2px',
                  background: '#e8ecf0', overflow: 'hidden', marginBottom: '4px',
                }}>
                  <div style={{
                    height: '100%', borderRadius: '2px',
                    width: `${(ackComment.length / 255) * 100}%`,
                    background: ackComment.length >= 230
                      ? '#dc3545'
                      : ackComment.length >= 150
                      ? '#fd7e14'
                      : '#0f4a45',
                    transition: 'width 0.1s, background 0.2s',
                  }}></div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#888' }}>
                    {ackComment.length === 0 && 'Please describe the action taken'}
                    {ackComment.length > 0 && ackComment.length < 10 && 'Too short — add more detail'}
                    {ackComment.length >= 10 && ackComment.length < 230 && 'Looking good'}
                    {ackComment.length >= 230 && (
                      <span style={{ color: '#dc3545' }}>Approaching character limit</span>
                    )}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: ackComment.length >= 230 ? '#dc3545' : '#888' }}>
                    {ackComment.length} / 255
                  </span>
                </div>
              </div>

              {/* Warning note */}
              <div style={{
                marginTop: '12px',
                background: '#fffbf0',
                border: '1px solid #fce08a',
                borderLeft: '3px solid #f6ad55',
                borderRadius: '7px',
                padding: '8px 12px',
                display: 'flex', alignItems: 'flex-start', gap: '8px',
              }}>
                <i className="fas fa-info-circle" style={{ color: '#dd6b20', fontSize: '13px', marginTop: '1px', flexShrink: 0 }}></i>
                <span style={{ fontSize: '12px', color: '#744210', lineHeight: '1.5' }}>
                  This acknowledgement is permanent and cannot be undone. Ensure the action taken is accurate before submitting.
                </span>
              </div>
            </div>

            {/* ── Footer ── */}
            <div style={{
              padding: '12px 20px 16px',
              borderTop: '1px solid #e8ecf0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              background: '#fafbfc',
            }}>
              <button
                style={{
                  background: 'white',
                  border: '1.5px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#495057',
                  cursor: isAcknowledging ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
                onClick={() => !isAcknowledging && setShowAckModal(false)}
                disabled={isAcknowledging}
              >
                <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
                Cancel
              </button>
              <button
                style={{
                  background: !ackComment.trim() || isAcknowledging
                    ? 'linear-gradient(135deg, #adb5bd 0%, #9aa0a6 100%)'
                    : 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: !ackComment.trim() || isAcknowledging ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '7px',
                  boxShadow: !ackComment.trim() || isAcknowledging ? 'none' : '0 3px 10px rgba(40,167,69,0.35)',
                  letterSpacing: '0.2px',
                }}
                disabled={!ackComment.trim() || isAcknowledging}
                onClick={() => acknowledgeAccident(ackComment.trim())}
              >
                {isAcknowledging ? (
                  <><span className="spinner-border spinner-border-sm" role="status"></span> Submitting...</>
                ) : (
                  <><i className="fas fa-check-circle" style={{ paddingLeft: 0 }}></i> Confirm &amp; Acknowledge</>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes blink {
          0%   { opacity: 1; }
          50%  { opacity: 0.6; }
          100% { opacity: 1; }
        }
        .blink-alert { animation: blink 1.5s infinite; }
        @keyframes fadeInPanel {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AccidentViewer;